import React, { useState, useEffect } from 'react';
import api from '../services/api';

const LicenseDetailsModal = ({ licenseId, onClose }) => {
  const [license, setLicense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  useEffect(() => {
    if (licenseId) {
      loadLicenseDetails();
    }
  }, [licenseId]);

  const loadLicenseDetails = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.getLicenseById(licenseId);
      if (response.success) {
        setLicense(response.data);
      } else {
        setError('Failed to load license details');
      }
    } catch (error) {
      setError(error.message || 'Failed to load license details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-success',
      expiring: 'bg-warning',
      expired: 'bg-danger',
      suspended: 'bg-secondary',
      cancelled: 'bg-dark',
    };
    return badges[status] || 'bg-secondary';
  };

  const handleResendEmail = async () => {
    if (!license || !license.contact_email) {
      setResendMessage('No contact email available for this license');
      return;
    }

    try {
      setResending(true);
      setResendMessage('');
      const response = await api.resendLicenseCertificateEmail(licenseId);
      
      if (response.success) {
        setResendMessage(`Email sent successfully to ${license.contact_email}`);
        // Clear message after 5 seconds
        setTimeout(() => setResendMessage(''), 5000);
      } else {
        setResendMessage(response.message || 'Failed to send email');
      }
    } catch (error) {
      setResendMessage(error.message || 'Failed to send email');
    } finally {
      setResending(false);
    }
  };

  if (!licenseId) return null;

  return (
    <div className="modal fade show" style={{ display: 'block', zIndex: 1050 }} tabIndex="-1">
      <div className="modal-dialog modal-lg" style={{ zIndex: 1051 }}>
        <div className="modal-content" style={{ backgroundColor: '#fff' }}>
          <div className="modal-header">
            <h4 className="modal-title">License Details</h4>
            <button type="button" className="close" onClick={onClose}>
              <span>&times;</span>
            </button>
          </div>
          <div className="modal-body">
            {loading ? (
              <div className="text-center p-4">
                <i className="fas fa-spinner fa-spin"></i> Loading...
              </div>
            ) : error ? (
              <div className="alert alert-danger">{error}</div>
            ) : license ? (
              <div>
                {/* Basic Information */}
                <div className="card mb-3">
                  <div className="card-header bg-primary">
                    <h5 className="card-title mb-0">Basic Information</h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <strong>License Key:</strong>
                        <div><code style={{ fontSize: '14px' }}>{license.license_key}</code></div>
                      </div>
                      <div className="col-md-6">
                        <strong>Status:</strong>
                        <div>
                          <span className={`badge ${getStatusBadge(license.status)}`}>
                            {license.status?.toUpperCase()}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="row mt-2">
                      <div className="col-md-6">
                        <strong>License Tier:</strong>
                        <div>
                          {license.tier_name ? (
                            <span className="badge bg-info">{license.tier_name}</span>
                          ) : (
                            <span className="text-muted">No tier assigned</span>
                          )}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <strong>Created By:</strong>
                        <div>{license.created_by_name || 'System'}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Company Information */}
                <div className="card mb-3">
                  <div className="card-header bg-info">
                    <h5 className="card-title mb-0">Company Information</h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <strong>Company Name:</strong>
                        <div>{license.company_name}</div>
                      </div>
                      <div className="col-md-6">
                        <strong>Contact Name:</strong>
                        <div>{license.contact_name || 'N/A'}</div>
                      </div>
                    </div>
                    <div className="row mt-2">
                      <div className="col-md-6">
                        <strong>Contact Email:</strong>
                        <div>
                          {license.contact_email || 'N/A'}
                          {license.contact_email && (
                            <button
                              type="button"
                              className="btn btn-sm btn-primary ml-2"
                              onClick={handleResendEmail}
                              disabled={resending}
                              title="Resend license certificate email"
                            >
                              {resending ? (
                                <>
                                  <i className="fas fa-spinner fa-spin"></i> Sending...
                                </>
                              ) : (
                                <>
                                  <i className="fas fa-envelope"></i> Resend Certificate
                                </>
                              )}
                            </button>
                          )}
                        </div>
                        {resendMessage && (
                          <div className={`mt-2 alert ${resendMessage.includes('successfully') ? 'alert-success' : 'alert-danger'}`} style={{ fontSize: '12px', padding: '5px 10px', marginTop: '5px' }}>
                            {resendMessage}
                          </div>
                        )}
                      </div>
                      <div className="col-md-6">
                        <strong>Contact Phone:</strong>
                        <div>{license.contact_phone || 'N/A'}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* License Details */}
                <div className="card mb-3">
                  <div className="card-header bg-success">
                    <h5 className="card-title mb-0">License Details</h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-4">
                        <strong>Start Date:</strong>
                        <div>{license.start_date ? new Date(license.start_date).toLocaleDateString() : 'N/A'}</div>
                      </div>
                      <div className="col-md-4">
                        <strong>Expiry Date:</strong>
                        <div>
                          {license.expiry_date ? (
                            <>
                              {new Date(license.expiry_date).toLocaleDateString()}
                              {new Date(license.expiry_date) < new Date() && (
                                <span className="badge bg-danger ml-2">Expired</span>
                              )}
                            </>
                          ) : (
                            'N/A'
                          )}
                        </div>
                      </div>
                      <div className="col-md-4">
                        <strong>Last Verified:</strong>
                        <div>
                          {license.last_verified_at 
                            ? new Date(license.last_verified_at).toLocaleString() 
                            : 'Never'}
                        </div>
                      </div>
                    </div>
                    <div className="row mt-2">
                      <div className="col-md-4">
                        <strong>Current Users:</strong>
                        <div>
                          <span className="badge bg-primary">{license.current_users || 0}</span>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <strong>Max Users:</strong>
                        <div>
                          <span className="badge bg-secondary">
                            {license.max_users === 999 ? 'Unlimited' : license.max_users || 1}
                          </span>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <strong>Usage:</strong>
                        <div>
                          {license.max_users > 0 ? (
                            <div className="progress" style={{ height: '20px' }}>
                              <div
                                className="progress-bar"
                                role="progressbar"
                                style={{
                                  width: `${((license.current_users || 0) / license.max_users) * 100}%`
                                }}
                              >
                                {Math.round(((license.current_users || 0) / license.max_users) * 100)}%
                              </div>
                            </div>
                          ) : (
                            'N/A'
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Additional Information */}
                <div className="card">
                  <div className="card-header bg-secondary">
                    <h5 className="card-title mb-0">Additional Information</h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <strong>Selected from Website:</strong>
                        <div>
                          {license.selected_from_website ? (
                            <span className="badge bg-success">Yes</span>
                          ) : (
                            <span className="badge bg-secondary">No</span>
                          )}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <strong>Selected At:</strong>
                        <div>
                          {license.selected_at 
                            ? new Date(license.selected_at).toLocaleString() 
                            : 'N/A'}
                        </div>
                      </div>
                    </div>
                    <div className="row mt-2">
                      <div className="col-md-6">
                        <strong>Created At:</strong>
                        <div>{license.created_at ? new Date(license.created_at).toLocaleString() : 'N/A'}</div>
                      </div>
                      <div className="col-md-6">
                        <strong>Updated At:</strong>
                        <div>{license.updated_at ? new Date(license.updated_at).toLocaleString() : 'N/A'}</div>
                      </div>
                    </div>
                    {license.notes && (
                      <div className="row mt-2">
                        <div className="col-md-12">
                          <strong>Notes:</strong>
                          <div className="mt-1 p-2 bg-light rounded" style={{ fontSize: '12px' }}>
                            {license.notes}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-default btn-sm" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1040 }} onClick={onClose}></div>
    </div>
  );
};

export default LicenseDetailsModal;

